import { useGlobalPlayer } from '@tunasong/audio-ui'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Close, Play } from '@tunasong/icons'
import { EmbedContent } from '@tunasong/plugin-embed/content'
import { parseLink } from '@tunasong/plugin-embed/parse-link'
import type { Audio, Persisted, PollOption, PollOptionAudio } from '@tunasong/schemas'
import { isAudio } from '@tunasong/schemas'
import { Button, IconButton, Typography } from '@tunasong/ui-lib'
import type { FC, ReactNode } from 'react'
import { createContext, useContext, useId, useState } from 'react'
import { serviceName } from './audio-candidates.js'
import { getOptionLabel } from './option-label.js'
import css from './option-audio.module.css'

interface PollAudio {
  /** The one option whose player window is open, if any. */
  openId: string | null
  /** Queue hosted audio in the app-wide player ("Play all"). */
  play(media: Persisted<Audio>[]): void
  setOpenId(id: string | null): void
}

const PollAudioContext = createContext<PollAudio | null>(null)

/**
 * Per poll view: the one global-player hook ("Play all") and which option's player window is open. One hook, not
 * one per option row: every `useGlobalPlayer` wires a controller to the shared <audio> element, so 175 rows would
 * multiply listeners. One open window, so two options never play over each other.
 */
export const PollAudioProvider: FC<{ pollId: string; children: ReactNode }> = props => {
  const { pollId, children } = props
  const { playlist } = useGlobalPlayer({ sourceEntityId: pollId })
  const [openId, setOpenId] = useState<string | null>(null)
  const value: PollAudio = { play: media => void playlist.playNow(media), openId, setOpenId }
  return <PollAudioContext value={value}>{children}</PollAudioContext>
}

/** Hosted audio in a plain audio player. The signed URL comes from the entity, fetched fresh when the window opens. */
const HostedPlayer: FC<{ entityId: string }> = props => {
  const { entityId } = props
  const { entity } = graphHooks.useEntity(entityId)
  if (!entity) {
    return <Typography variant="caption">Loading audio…</Typography>
  }
  const url = isAudio(entity) ? entity.storageUrls?.url : undefined
  if (!url) {
    return <Typography variant="caption">This audio is not available.</Typography>
  }
  return <audio className={css.audio} src={url} controls preload="metadata" title={entity.name} />
}

/** A link in the service's own embedded player; a link nothing can embed is offered as a link, inside the window. */
const ExternalPlayer: FC<{ service: string; url: string }> = props => {
  const { service, url } = props
  const embed = parseLink(url)
  if (!embed) {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        Open on {serviceName(service)}
      </a>
    )
  }
  return <EmbedContent element={{ ...embed, size: 'small' }} />
}

const sourceOf = (audio: PollOptionAudio) => (audio.kind === 'entity' ? 'recording' : serviceName(audio.service))

export interface OptionAudioProps {
  option: Pick<PollOption, 'name' | 'entityRef' | 'audio'>
}

/**
 * The option's play control: one Play toggle, whatever backs the audio, that opens a small player window under the
 * option — a plain audio player for hosted audio, the service's embed for Spotify, YouTube and the like. Nothing
 * leaves the app unasked, and nothing plays until the window's own controls are used.
 *
 * Never render this inside a <label> (see WithOptionAudio): a click anywhere in a label can activate its control,
 * so pressing play would cast or withdraw a vote. No wrapper element either: the toggle and the window are items
 * of the row they are rendered into, and only the window takes a line of its own.
 */
export const OptionAudio: FC<OptionAudioProps> = props => {
  const { option } = props
  const { audio } = option
  const poll = useContext(PollAudioContext)
  const id = useId()
  const { entity: linked } = graphHooks.useEntity(option.entityRef)
  /** What the option is called on screen, for the accessible names. */
  const label = getOptionLabel(option, linked?.name)

  if (!(audio && poll)) {
    return null
  }
  const isOpen = poll.openId === id
  const handleToggle = () => poll.setOpenId(isOpen ? null : id)

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Play ${label}`}
        aria-expanded={isOpen}
        title={isOpen ? 'Close player' : `Play (${sourceOf(audio)})`}
        onClick={handleToggle}
      >
        {isOpen ? <Close /> : <Play />}
      </IconButton>
      {isOpen ? (
        <div role="region" aria-label={`Player for ${label}`} className={css.player}>
          {audio.kind === 'entity' ? (
            <HostedPlayer entityId={audio.entityId} />
          ) : (
            <ExternalPlayer service={audio.service} url={audio.url} />
          )}
        </div>
      ) : null}
    </>
  )
}

/**
 * A labelled control (vote button, radio, checkbox) with the option's audio beside it, outside the <label>.
 * Options without audio render the control alone, unchanged.
 */
export const WithOptionAudio: FC<{ option: PollOption; children: ReactNode }> = props => {
  const { option, children } = props
  if (!option.audio) {
    return children
  }
  return (
    <div className={css.row}>
      {children}
      <OptionAudio option={option} />
    </div>
  )
}

/** Queue every live option's hosted audio, in option order. External links cannot join the global player. */
export const PlayAllButton: FC<{ options: PollOption[] }> = props => {
  const { options } = props
  const player = useContext(PollAudioContext)
  const ids = options.flatMap(option =>
    !option.removed && option.audio?.kind === 'entity' ? [option.audio.entityId] : []
  )
  const { entities } = graphHooks.useEntitiesById<Audio>(ids)
  const byId = new Map(entities.filter(isAudio).map(entity => [entity.id, entity]))
  const queue = ids.flatMap(id => {
    const entity = byId.get(id)
    return entity ? [entity] : []
  })

  if (!player || queue.length === 0) {
    return null
  }
  return (
    <Button size="small" startIcon={<Play />} onClick={() => player.play(queue)}>
      Play all
    </Button>
  )
}
